import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { MultiSelect, type MultiSelectOption } from '@/components/ui/multiselect';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { api, ApiError } from '@/lib/api';
import {
  SPORT_IDS,
  getSportLabel,
  getPopularCities,
  type SportId,
} from '@huddle/shared';
import { ArrowLeft, AlertTriangle, Users, CalendarDays, MapPin } from 'lucide-react';

const RU_KZ_TIMEZONES: ComboboxOption[] = [
  { value: 'Europe/Kaliningrad', label: 'Europe/Kaliningrad', hint: 'GMT+2' },
  { value: 'Europe/Moscow', label: 'Europe/Moscow', hint: 'GMT+3' },
  { value: 'Europe/Samara', label: 'Europe/Samara', hint: 'GMT+4' },
  { value: 'Asia/Yekaterinburg', label: 'Asia/Yekaterinburg', hint: 'GMT+5' },
  { value: 'Asia/Omsk', label: 'Asia/Omsk', hint: 'GMT+6' },
  { value: 'Asia/Novosibirsk', label: 'Asia/Novosibirsk', hint: 'GMT+7' },
  { value: 'Asia/Krasnoyarsk', label: 'Asia/Krasnoyarsk', hint: 'GMT+7' },
  { value: 'Asia/Irkutsk', label: 'Asia/Irkutsk', hint: 'GMT+8' },
  { value: 'Asia/Yakutsk', label: 'Asia/Yakutsk', hint: 'GMT+9' },
  { value: 'Asia/Vladivostok', label: 'Asia/Vladivostok', hint: 'GMT+10' },
  { value: 'Asia/Magadan', label: 'Asia/Magadan', hint: 'GMT+11' },
  { value: 'Asia/Kamchatka', label: 'Asia/Kamchatka', hint: 'GMT+12' },
  { value: 'Asia/Almaty', label: 'Asia/Almaty', hint: 'GMT+6' },
  { value: 'Asia/Aqtobe', label: 'Asia/Aqtobe', hint: 'GMT+5' },
  { value: 'Asia/Atyrau', label: 'Asia/Atyrau', hint: 'GMT+5' },
];

interface Member {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  role: string;
  joinedAt: string;
}

interface Detail {
  id: string;
  name: string;
  slug: string | null;
  sportTypes: string[];
  logoUrl: string | null;
  description: string | null;
  city: string | null;
  cityId: string | null;
  country: string | null;
  timezone: string;
  locale: string;
  plan: string;
  maxMembers: number;
  joinMode: string;
  telegramChatId: string | null;
  lat: number | null;
  lng: number | null;
  createdBy: string;
  createdAt: string;
  memberCount: number;
  venueCount: number;
  activityCount: number;
  members: Member[];
  attachedVenueIds: string[];
}

type Role = 'owner' | 'admin' | 'member' | 'guest' | 'pending';
const ROLES: Role[] = ['owner', 'admin', 'member', 'guest', 'pending'];

function memberName(m: Member) {
  return [m.firstName, m.lastName].filter(Boolean).join(' ') || m.email || m.userId.slice(0, 8);
}

interface DeleteCascade {
  members: number;
  activities: number;
  venues: number;
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-medium mb-3">
      {children}
    </div>
  );
}

export function OrgDetailPage() {
  const { id } = useParams({ from: '/orgs/$id' });
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'org', id],
    queryFn: () =>
      api.get<{ data: Detail }>(`/api/admin/orgs/${id}`).then((r) => r.data),
  });

  const [form, setForm] = useState<Partial<Detail> | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteCascade, setDeleteCascade] = useState<DeleteCascade | null>(null);
  const [ownerTransferUserId, setOwnerTransferUserId] = useState<string | null>(null);
  const [ownerConfirmTyped, setOwnerConfirmTyped] = useState('');
  const [pendingMemberId, setPendingMemberId] = useState<string | null>(null);

  useEffect(() => { if (data) setForm(data); }, [data]);

  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      api.patch<{ data: Detail }>(`/api/admin/orgs/${id}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'org', id] });
      qc.invalidateQueries({ queryKey: ['admin', 'orgs'] });
    },
  });

  const del = useMutation({
    mutationFn: (force: boolean) =>
      api.delete<{ data: { ok: true; cascaded: DeleteCascade } }>(
        `/api/admin/orgs/${id}?confirm=DELETE${force ? '&force=true' : ''}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'orgs'] });
      navigate({ to: '/orgs' });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'HAS_DEPENDENTS') {
        const details = err.details as DeleteCascade | undefined;
        if (details) setDeleteCascade(details);
      }
    },
  });

  const kick = useMutation({
    mutationFn: (userId: string) =>
      api.post<{ data: { ok: true } }>(`/api/admin/orgs/${id}/members/${userId}/kick`, {}),
    onMutate: (userId) => setPendingMemberId(userId),
    onSettled: () => setPendingMemberId(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'org', id] });
      qc.invalidateQueries({ queryKey: ['admin', 'orgs'] });
    },
  });

  const setRole = useMutation({
    mutationFn: (args: { userId: string; role: Role; confirm?: string }) =>
      api.post<{ data: { ok: true } }>(
        `/api/admin/orgs/${id}/members/${args.userId}/role`,
        { role: args.role, confirm: args.confirm },
      ),
    onMutate: (args) => setPendingMemberId(args.userId),
    onSettled: () => setPendingMemberId(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'org', id] });
      qc.invalidateQueries({ queryKey: ['admin', 'orgs'] });
      setOwnerTransferUserId(null);
      setOwnerConfirmTyped('');
    },
  });

  const patchPayload = useMemo(() => {
    if (!form || !data) return {} as Record<string, unknown>;
    const out: Record<string, unknown> = {};

    const scalarKeys: (keyof Detail)[] = [
      'name', 'slug', 'description', 'city', 'cityId', 'country',
      'timezone', 'locale', 'plan', 'maxMembers', 'joinMode',
    ];
    for (const k of scalarKeys) {
      if (form[k] !== data[k]) out[k] = form[k] ?? null;
    }
    const formSports = (form.sportTypes ?? []).join(',');
    const dataSports = (data.sportTypes ?? []).join(',');
    if (formSports !== dataSports) out.sportTypes = form.sportTypes ?? [];
    if (
      form.lat != null && form.lng != null
      && (form.lat !== data.lat || form.lng !== data.lng)
    ) {
      out.lat = form.lat;
      out.lng = form.lng;
    }
    return out;
  }, [form, data]);

  if (isLoading) return <div className="p-4 sm:p-6 lg:p-8 text-zinc-500 text-sm">Загрузка…</div>;
  if (error || !data || !form) return <div className="p-4 sm:p-6 lg:p-8 text-rose-300 text-sm">Ошибка загрузки</div>;

  const sportOptions: MultiSelectOption[] = SPORT_IDS.map((id) => ({
    value: id,
    label: getSportLabel(id, 'ru'),
    hint: id,
  }));
  const cityOptions: ComboboxOption[] = getPopularCities('RU', 200).map((c) => ({
    value: c.id,
    label: c.name,
    hint: c.id,
  }));

  const isOrphan = data.memberCount <= 1 && data.activityCount === 0;
  const cascade = deleteCascade ?? {
    members: Math.max(0, data.memberCount - 1),
    activities: data.activityCount,
    venues: data.venueCount,
  };
  const needsForce = !isOrphan;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl space-y-6">
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate({ to: '/orgs' })}
        className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-300 transition-colors"
      >
        <ArrowLeft className="h-3 w-3" /> К списку
      </button>

      {/* Hero */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          {data.logoUrl ? (
            // eslint-disable-next-line jsx-a11y/alt-text
            <img
              src={data.logoUrl}
              alt={data.name}
              className="h-16 w-16 rounded-md object-cover bg-zinc-900 border border-zinc-800"
            />
          ) : (
            <div className="h-16 w-16 rounded-md bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-600 text-[10px] uppercase tracking-wider">
              нет лого
            </div>
          )}
          <div className="space-y-2 flex-1 min-w-0">
            <h1 className="text-2xl font-semibold tracking-[-0.01em] text-zinc-50 truncate">{data.name}</h1>
            <div className="flex items-center gap-2 flex-wrap text-sm">
              {data.sportTypes.map((s) => (
                <Badge key={s} variant="secondary">{s}</Badge>
              ))}
              <Badge variant="outline">{data.plan}</Badge>
              {data.city ? <span className="text-xs text-zinc-500">· {data.city}</span> : null}
            </div>
            <div className="font-mono text-[11px] text-zinc-500 tabular-nums">
              {data.slug ? <>/{data.slug} · </> : ''}
              создал {data.createdBy.slice(0, 8)} ·{' '}
              {new Date(data.createdAt).toLocaleString('ru')}
            </div>
          </div>
        </div>
        <div className="shrink-0">
          <Dialog
            open={deleteOpen}
            onOpenChange={(v) => {
              setDeleteOpen(v);
              if (!v) setDeleteCascade(null);
            }}
          >
            <DialogTrigger asChild>
              <Button variant="destructive">Удалить</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Удалить клуб?</DialogTitle>
              </DialogHeader>
              <div className="text-sm text-zinc-400 space-y-3">
                <div className="text-xs uppercase tracking-[0.14em] text-zinc-500 font-medium">
                  Зависимости
                </div>
                <ul className="space-y-1">
                  <DepRow icon={<Users className="h-3.5 w-3.5" />} label="Участников" value={cascade.members} />
                  <DepRow icon={<CalendarDays className="h-3.5 w-3.5" />} label="Игр" value={cascade.activities} />
                  <DepRow icon={<MapPin className="h-3.5 w-3.5" />} label="Площадок (привязано)" value={cascade.venues} />
                </ul>
                <div className={isOrphan ? 'text-zinc-400' : 'inline-flex items-center gap-2 text-amber-300'}>
                  {!isOrphan && <AlertTriangle className="h-4 w-4" />}
                  {isOrphan
                    ? 'Клуб пустой, удаляется чисто.'
                    : 'Cascade-удалит участников, игры и связи с площадками. Включи force, чтобы продолжить.'}
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="destructive"
                  disabled={del.isPending}
                  onClick={() => del.mutate(needsForce)}
                >
                  {del.isPending
                    ? '...'
                    : needsForce
                      ? 'Удалить (force)'
                      : 'Удалить'}
                </Button>
              </DialogFooter>
              {del.isError && (
                <div className="text-rose-300 text-xs mt-2">
                  {del.error instanceof ApiError ? del.error.message : 'Ошибка удаления'}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats card */}
      <Card>
        <CardContent className="pt-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Stat label="Участников" value={data.memberCount} />
            <Stat label="Игр" value={data.activityCount} />
            <Stat label="Площадок" value={data.venueCount} />
          </div>
          <div className="mt-4 pt-4 border-t border-zinc-900 flex flex-wrap gap-x-6 gap-y-1 text-xs text-zinc-500">
            <span>Часовой пояс: <span className="font-mono text-zinc-300">{data.timezone}</span></span>
            <span>Locale: <span className="font-mono text-zinc-300">{data.locale}</span></span>
            {data.telegramChatId ? (
              <span>TG chat: <code className="font-mono text-zinc-300">{data.telegramChatId}</code></span>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {/* Edit form */}
      <Card>
        <CardHeader>
          <CardTitle>Параметры клуба</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <SectionHeading>Идентификация</SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Название</Label>
                <Input
                  value={form.name ?? ''}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Slug</Label>
                <Input
                  className="font-mono"
                  value={form.slug ?? ''}
                  onChange={(e) => setForm({ ...form, slug: e.target.value || null })}
                />
              </div>
              <div className="space-y-1.5 col-span-1 sm:col-span-2">
                <Label>Описание</Label>
                <Input
                  value={form.description ?? ''}
                  onChange={(e) => setForm({ ...form, description: e.target.value || null })}
                />
              </div>
              <div className="space-y-1.5 col-span-1 sm:col-span-2">
                <Label>Виды спорта</Label>
                <MultiSelect
                  options={sportOptions}
                  value={(form.sportTypes ?? []) as SportId[]}
                  onChange={(v) => setForm({ ...form, sportTypes: v })}
                  placeholder="Выбери виды спорта"
                  searchPlaceholder="Поиск спорта…"
                />
              </div>
            </div>
          </div>

          <div>
            <SectionHeading>Локация</SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Город (text)</Label>
                <Input
                  value={form.city ?? ''}
                  onChange={(e) => setForm({ ...form, city: e.target.value || null })}
                  placeholder="если города нет в списке"
                />
              </div>
              <div className="space-y-1.5">
                <Label>city_id</Label>
                <Combobox
                  options={cityOptions}
                  value={form.cityId ?? null}
                  onChange={(v) => setForm({ ...form, cityId: v })}
                  placeholder="Выбери город"
                  searchPlaceholder="Поиск города…"
                  emptyText="Город не найден"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Country</Label>
                <Input
                  className="font-mono"
                  value={form.country ?? ''}
                  onChange={(e) => setForm({ ...form, country: e.target.value || null })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Timezone (IANA)</Label>
                <Combobox
                  options={RU_KZ_TIMEZONES}
                  value={form.timezone ?? null}
                  onChange={(v) => setForm({ ...form, timezone: v ?? '' })}
                  placeholder="Europe/Moscow"
                  searchPlaceholder="Поиск таймзоны…"
                  clearable={false}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Lat</Label>
                <Input
                  className="font-mono"
                  type="number"
                  step="0.000001"
                  value={form.lat ?? ''}
                  onChange={(e) =>
                    setForm({ ...form, lat: e.target.value === '' ? null : Number(e.target.value) })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>Lng</Label>
                <Input
                  className="font-mono"
                  type="number"
                  step="0.000001"
                  value={form.lng ?? ''}
                  onChange={(e) =>
                    setForm({ ...form, lng: e.target.value === '' ? null : Number(e.target.value) })
                  }
                />
              </div>
            </div>
          </div>

          <div>
            <SectionHeading>Настройки</SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Locale</Label>
                <Select
                  value={form.locale ?? 'ru'}
                  onValueChange={(v) => setForm({ ...form, locale: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ru">ru</SelectItem>
                    <SelectItem value="en">en</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Plan</Label>
                <Select
                  value={form.plan ?? 'free'}
                  onValueChange={(v) => setForm({ ...form, plan: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">free</SelectItem>
                    <SelectItem value="pro">pro</SelectItem>
                    <SelectItem value="league">league</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Max участников</Label>
                <Input
                  className="font-mono"
                  type="number"
                  value={form.maxMembers ?? 0}
                  onChange={(e) => setForm({ ...form, maxMembers: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Join mode</Label>
                <Select
                  value={form.joinMode ?? 'open'}
                  onValueChange={(v) => setForm({ ...form, joinMode: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">open</SelectItem>
                    <SelectItem value="invite">invite</SelectItem>
                    <SelectItem value="approval">approval</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="flex gap-3 items-center pt-2 border-t border-zinc-900">
            <Button
              onClick={() => save.mutate(patchPayload)}
              disabled={save.isPending || Object.keys(patchPayload).length === 0}
            >
              {save.isPending ? 'Сохранение…' : 'Сохранить'}
            </Button>
            <span className="text-[11px] text-zinc-500">
              {Object.keys(patchPayload).length > 0
                ? <>Изменено: <span className="font-mono">{Object.keys(patchPayload).join(', ')}</span></>
                : 'Нет изменений'}
            </span>
            {save.isError && (
              <span className="text-rose-300 text-sm self-center">
                {save.error instanceof ApiError ? save.error.message : 'Ошибка сохранения'}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Members */}
      <Card>
        <CardHeader>
          <CardTitle>
            Участники
            <span className="ml-2 text-[11px] font-normal text-zinc-500 font-mono tabular-nums">
              {data.members.length}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.members.length === 0 ? (
            <div className="text-xs text-zinc-500">Никого нет.</div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-zinc-800/80">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 bg-bg-1">
                  <tr className="border-b border-zinc-900">
                    <th className="text-left py-2 pl-4 pr-4 font-medium">Игрок</th>
                    <th className="text-left py-2 pr-4 font-medium">Email</th>
                    <th className="text-left py-2 pr-4 font-medium">Роль</th>
                    <th className="text-left py-2 pr-4 font-medium">Вступил</th>
                    <th className="text-right py-2 pr-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.members.map((m) => {
                    const isPending = pendingMemberId === m.userId;
                    return (
                      <tr key={m.userId} className="border-b border-zinc-900/80 last:border-0 hover:bg-zinc-900/40">
                        <td className="py-2 pl-4 pr-4 text-zinc-200">{memberName(m)}</td>
                        <td className="py-2 pr-4 text-zinc-500 text-xs">{m.email ?? '—'}</td>
                        <td className="py-2 pr-4">
                          <Select
                            value={m.role}
                            onValueChange={(v) => {
                              const role = v as Role;
                              if (role === m.role) return;
                              if (role === 'owner') {
                                setOwnerTransferUserId(m.userId);
                                setOwnerConfirmTyped('');
                                return;
                              }
                              setRole.mutate({ userId: m.userId, role });
                            }}
                            disabled={isPending}
                          >
                            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {ROLES.map((r) => (
                                <SelectItem key={r} value={r}>{r}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-2 pr-4 text-zinc-500 text-xs font-mono tabular-nums">
                          {new Date(m.joinedAt).toLocaleDateString('ru')}
                        </td>
                        <td className="py-2 pr-4 text-right">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={isPending}
                              >
                                Кикнуть
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Кикнуть {memberName(m)}?</DialogTitle>
                              </DialogHeader>
                              <div className="text-sm text-zinc-400 space-y-1">
                                <div>Это нельзя отменить. Действие будет записано в аудит.</div>
                                <div>
                                  Будут удалены записи юзера в{' '}
                                  <code className="font-mono text-zinc-200">activity_signups</code> и{' '}
                                  <code className="font-mono text-zinc-200">activity_participants</code> по
                                  играм этого клуба.
                                </div>
                              </div>
                              <DialogFooter>
                                <Button
                                  variant="destructive"
                                  disabled={kick.isPending}
                                  onClick={() => kick.mutate(m.userId)}
                                >
                                  {kick.isPending ? '...' : 'Кикнуть'}
                                </Button>
                              </DialogFooter>
                              {kick.isError && (
                                <div className="text-rose-300 text-xs mt-2">
                                  {kick.error instanceof ApiError ? kick.error.message : 'Ошибка'}
                                </div>
                              )}
                            </DialogContent>
                          </Dialog>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Owner transfer dialog */}
      <Dialog
        open={ownerTransferUserId !== null}
        onOpenChange={(v) => {
          if (!v) {
            setOwnerTransferUserId(null);
            setOwnerConfirmTyped('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Передать ownership?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-zinc-400 space-y-2">
            <div>
              Текущий owner будет понижен до <code className="font-mono text-zinc-200">admin</code>.
              Новым owner станет:{' '}
              <strong className="text-zinc-200">
                {(() => {
                  const m = data.members.find((x) => x.userId === ownerTransferUserId);
                  return m ? memberName(m) : '—';
                })()}
              </strong>.
            </div>
            <Label>Введи <code className="font-mono text-zinc-200">TRANSFER_OWNERSHIP</code></Label>
            <Input
              className="font-mono"
              value={ownerConfirmTyped}
              onChange={(e) => setOwnerConfirmTyped(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              variant="destructive"
              disabled={
                ownerConfirmTyped !== 'TRANSFER_OWNERSHIP'
                || setRole.isPending
                || !ownerTransferUserId
              }
              onClick={() => {
                if (!ownerTransferUserId) return;
                setRole.mutate({
                  userId: ownerTransferUserId,
                  role: 'owner',
                  confirm: 'TRANSFER_OWNERSHIP',
                });
              }}
            >
              Передать
            </Button>
          </DialogFooter>
          {setRole.isError && (
            <div className="text-rose-300 text-xs mt-2">
              {setRole.error instanceof ApiError ? setRole.error.message : 'Ошибка'}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DepRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <li className="flex items-center justify-between text-xs">
      <span className="inline-flex items-center gap-1.5 text-zinc-400">
        <span className="text-zinc-600">{icon}</span>
        {label}
      </span>
      <span className="font-mono tabular-nums text-zinc-200">{value}</span>
    </li>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-zinc-800/80 bg-bg-1 px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-medium">
        {label}
      </div>
      <div className="mt-1 font-mono text-xl tabular-nums font-medium text-zinc-100">
        {value.toLocaleString('ru')}
      </div>
    </div>
  );
}
